import type { AmbientKind } from '../core/types';
import { fadeInLevel, highpass, normalize } from './dsp';
import { audioContext } from './fx';

/**
 * Looping, synthesized background beds that help mask distractions during focus.
 *
 *   voice (source → [filter] → fade gain) ─┐
 *   voice (fading out)                     ├─▶ bus gain (volume) ─▶ limiter ─▶ destination
 *
 * Each voice's fade gain is only ever driven by schedules created here, so its current level is
 * computed (`fadeInLevel`), never read back: `AudioParam.value` returns the default 1.0 for a node
 * that has not rendered yet, which used to start a fade-out at full volume — a loud burst on every
 * switch. Volume lives on the shared bus, so moving the slider never fights a fade.
 */
type Kind = Exclude<AmbientKind, 'off'>;

interface Voice {
  kind: Kind;
  src: AudioBufferSourceNode;
  fade: GainNode;
  start: number;
}

const FADE_IN = 1.2;
const FADE_OUT = 0.6;
/** Every bed is normalised to this loudness (and peak) so switching never jumps in level. */
const TARGET_RMS = 0.15;
const MAX_PEAK = 0.9;

const buffers = new Map<Kind, AudioBuffer>();
let bus: { ctx: AudioContext; gain: GainNode } | null = null;
let voice: Voice | null = null;
let level = 0.4;
let previewTimer: ReturnType<typeof setTimeout> | undefined;

const busLevel = () => level * 0.6;

function build(c: AudioContext, kind: Kind): AudioBuffer {
  const seconds = kind === 'rain' ? 7 : 5;
  const len = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, len, c.sampleRate);
  const channels: Float32Array[] = [];
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    channels.push(d);
    if (kind === 'brown') {
      let last = 0;
      for (let i = 0; i < len; i++) {
        last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02;
        d[i] = last * 3.2;
      }
      highpass(d, c.sampleRate, 25); // a random walk drifts: keep the woofer from pumping
    } else {
      // Paul Kellet's pink-noise filter
      let b0 = 0,
        b1 = 0,
        b2 = 0,
        b3 = 0,
        b4 = 0,
        b5 = 0,
        b6 = 0;
      for (let i = 0; i < len; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.969 * b2 + w * 0.153852;
        b3 = 0.8665 * b3 + w * 0.3104856;
        b4 = 0.55 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.016898;
        d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
        b6 = w * 0.115926;
      }
      if (kind === 'rain') {
        // sprinkle decaying droplets over a softened pink bed
        for (let i = 0; i < len; i++) d[i]! *= 0.55;
        const drops = seconds * 90;
        for (let n = 0; n < drops; n++) {
          const at = Math.floor(Math.random() * len);
          const amp = 0.05 + Math.random() * 0.25;
          const dl = Math.floor(c.sampleRate * (0.004 + Math.random() * 0.02));
          for (let j = 0; j < dl && at + j < len; j++)
            d[at + j]! += (Math.random() * 2 - 1) * amp * Math.exp((-6 * j) / dl);
        }
      }
    }
    // short crossfade at the loop seam
    const xf = Math.floor(c.sampleRate * 0.05);
    for (let i = 0; i < xf; i++) {
      const k = i / xf;
      d[i] = d[i]! * k + d[len - xf + i]! * (1 - k);
    }
  }
  normalize(channels, TARGET_RMS, MAX_PEAK);
  return buf;
}

function getBus(c: AudioContext): GainNode {
  if (bus?.ctx !== c) {
    const gain = c.createGain();
    gain.gain.value = busLevel();
    // Safety net: overlapping crossfades at full volume can never clip or blast.
    const limiter = c.createDynamicsCompressor();
    limiter.threshold.value = -3;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.2;
    gain.connect(limiter).connect(c.destination);
    bus = { ctx: c, gain };
  }
  return bus.gain;
}

/** Fades the current voice out from wherever its fade-in has reached. */
function release() {
  const v = voice;
  voice = null;
  if (!v) return;
  const t = v.fade.context.currentTime;
  const g = v.fade.gain;
  g.cancelScheduledValues(t);
  g.setValueAtTime(fadeInLevel(v.start, t, FADE_IN), t);
  g.linearRampToValueAtTime(0, t + FADE_OUT);
  v.src.stop(t + FADE_OUT + 0.05);
}

export function setAmbientVolume(v: number) {
  level = v / 100;
  if (bus) bus.gain.gain.setTargetAtTime(busLevel(), bus.ctx.currentTime, 0.05);
}

/** Crossfades to `kind` ('off' fades out). A no-op if it is already playing. */
export function playAmbient(kind: AmbientKind) {
  if (kind === (voice?.kind ?? 'off')) return;
  release();
  if (kind === 'off') return;
  const c = audioContext();
  if (!c) return;
  let buf = buffers.get(kind);
  if (!buf) {
    buf = build(c, kind);
    buffers.set(kind, buf);
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const fade = c.createGain();
  const start = c.currentTime;
  fade.gain.setValueAtTime(0, start);
  fade.gain.linearRampToValueAtTime(1, start + FADE_IN);
  let node: AudioNode = src;
  if (kind === 'rain') {
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 5200;
    node = node.connect(lp);
  }
  node.connect(fade).connect(getBus(c));
  src.onended = () => {
    src.disconnect();
    fade.disconnect();
  };
  src.start(start);
  voice = { kind, src, fade, start };
}

export function stopAmbient() {
  clearTimeout(previewTimer);
  release();
}

/**
 * Brief audition from the settings panel. Keeps playing (no restart) if `kind` is already on —
 * e.g. during focus — and `onEnd` then settles on whatever should be playing.
 */
export function previewAmbient(kind: AmbientKind, onEnd: () => void, ms = 3500) {
  clearTimeout(previewTimer);
  playAmbient(kind);
  previewTimer = setTimeout(onEnd, ms);
}

export const ambientPlaying = (): AmbientKind => voice?.kind ?? 'off';
